import React, { useState, Fragment, useEffect, useMemo, useCallback } from 'react';

import { getTutorials } from '../../../store/tutorials';
import { TABLE_ITEM_COUNT_LIMITS, tutorialConstants } from '../../../utils/constants';

import { getCoverData } from '../../../store/coverData';
import { getReportDetails, setItemsPerPage } from '../../../store/reportDetails';
import { useAppDispatch, useAppSelector } from '../../../store/configureStore';
import { CDRInformationModel, CDRReviewInformation } from '../../forms/custom-cdr/schema';
import { AnalysisItem } from '../../forms/custom-cdr/threatAnalysis';

import Button from '../../themed/button';
import CDRReportNumberForm from '../../forms/custom-cdr/reportCDRNumber';
import CDRInformation from '../../forms/custom-cdr/information';
import CDRReviewAndApproval from '../../forms/custom-cdr/reviewAndApproval';
import CDRReport from '.';
import Conditional from '../../common/conditional';
import LoadingBadge from '../../themed/loading-badge';
import Elevation from '../../themed/instruction-modals/elevation';
import Modal from '../../themed/modal';
import SyncTime from '../../blocks/sync-time';
import ThreatAnalysisEvaluationItems from './threatAnalysisEvaluationItems';
import WarningNotice from '../custom-visual-inspection/warning-notice';
import Zippy from '../../themed/zippy';

import useAutoSaveReport from '../../../hooks/useAutoSave';
import useInspectedItemIDUpdate from '../../../hooks/useInspectedItemIDUpdate';
import useProjectDetails from '../../../hooks/useProjectDetails';

import config from '../../../utils/config';
import toastService from '../../../services/toastService';

const CustomCDRReport: React.FC = () => {
	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);
	const { fviCoverData } = useAppSelector(getCoverData);
	const { isSaving } = useAppSelector(getReportDetails);

	const [previewReport, setPreviewReport] = useState(false);
    const [analysisItems, setAnalysisItems] = useState<AnalysisItem[]>([]);
	const [cdrReportNumber, setCDRReportNumber] = useState('');
    const [cdrInformation, setCDRInformation] = useState<CDRInformationModel>();
    const [reviewInformation, setReviewInformation] = useState<CDRReviewInformation>();

	const reportDetails = useMemo(() => {
		return {
            analysisItems,
            cdrReportNumber,
            cdrInformation,
            reviewInformation,
			projectDetails,
		};
	}, [analysisItems, cdrInformation, cdrReportNumber, reviewInformation, projectDetails]);

	const validationMessage = useMemo(() => {
		if (!reportDetails.cdrReportNumber) {
            return 'Enter a valid CDR number!';
        }
		
        if (!reportDetails.cdrInformation || !reportDetails.cdrInformation.pidNumber) {
            return 'Complete the Information Section!';
        }
        
        if (reportDetails.analysisItems.length === 0) {
            return 'Complete the Threat Analysis Section!';
        }

        if (!reportDetails.reviewInformation || !reportDetails.reviewInformation.onSiteEngineerName) {
            return 'Complete the Information Section!';
        }

		return '';
	}, [reportDetails]);

	const autoSave = useAutoSaveReport(reportDetails);
	const dispatch = useAppDispatch();

	const togglePreviewReport = useCallback((): void => {
		if (config.enableReportCompletionBeforePublish) {
			if (validationMessage) return toastService.error(validationMessage);
		}

		setPreviewReport((p: boolean) => !p);
	}, [validationMessage]);

	const handleSaveAndPreview = useCallback((): void => {
		autoSave.saveReport();
		togglePreviewReport();
	}, [autoSave, togglePreviewReport]);

	useEffect(() => {
		function initializeReport() {
            const cachedReport = autoSave.oldReportData as typeof reportDetails;
            if (!cachedReport) return null;

            setAnalysisItems(cachedReport.analysisItems);
            setCDRReportNumber(cachedReport.cdrReportNumber);
            setCDRInformation(cachedReport.cdrInformation);
			setReviewInformation(cachedReport.reviewInformation);
		} 

		initializeReport();
	}, [autoSave.oldReportData, dispatch, fviCoverData]);

	useEffect(() => {
		dispatch(setItemsPerPage(TABLE_ITEM_COUNT_LIMITS.DRT));
	}, [dispatch]);

	useInspectedItemIDUpdate(analysisItems.map((item) => item.cdrNumber));

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
               <CDRReport {...reportDetails} />
			</Modal>

            <div className="mb-4">
				<SyncTime lastSynced={autoSave.lastSaved} />
			</div>

			<Conditional isVisible={validationMessage.length > 0}>
				<WarningNotice title='Incomplete Report' body={validationMessage} />
			</Conditional>

			<Zippy headerTitle="Report CDR Number" isValid={cdrReportNumber.length > 0}>
				<CDRReportNumberForm reportNumber={cdrReportNumber} onSaveReportNumber={setCDRReportNumber} />
			</Zippy>

            <Zippy headerTitle="Information" isValid={cdrInformation ? Boolean(cdrInformation.pidNumber) : false}>
				<CDRInformation cdrInformation={cdrInformation} onSaveCDRInformation={setCDRInformation} />
			</Zippy>
            
            <Zippy headerTitle="Threat Analysis & Evaluation" isValid={analysisItems.length > 0}>
                <ThreatAnalysisEvaluationItems analysisItems={analysisItems} onChangeAnalysisItems={setAnalysisItems} />
			</Zippy>

            <Zippy headerTitle="Review Information" isValid={reviewInformation ? Boolean(reviewInformation.onSiteEngineerName) : false}>
				<CDRReviewAndApproval reviewInformation={reviewInformation} onSaveReviewInformation={setReviewInformation} />
			</Zippy>

			<div className="d-flex justify-content-between align-items-center">
				<div>
					<Conditional isVisible={autoSave.isProcessing}>
						<LoadingBadge message="Saving" />
					</Conditional>
				</div>

				<Elevation isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}>
					<Button
						label="Save and Preview Report"
						disabled={error || autoSave.isProcessing || isSaving }
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CustomCDRReport;
